// Centralizes the existing website "source" (attribution) handling.
//
// The source of truth is the same logic the site already used in App.js:
//   1. the `source` query parameter (e.g. ?source=google), otherwise
//   2. the external referrer (document.referrer when it is not this site).
//
// This util only ADDS retention so the already-detected source survives
// in-session navigation (the landing page detects it, but the Desktop launch
// happens later on the Access ADA page). It does not invent or transform the
// source. Fallback is "unknown" and is only used when nothing was detected.

export const SOURCE_STORAGE_KEY = "fd_source";
export const SOURCE_FALLBACK = "unknown";

// Explicit marker telling the desktop app which channel launched it. When the
// desktop app is opened from the website this is always "website", so the
// desktop access log can clearly record that it came from the website,
// independent of the attribution `source` value (google / referrer / origin).
export const LAUNCHED_FROM_WEBSITE = "website";

// When no attribution source (e.g. google/facebook/referrer) was ever detected,
// the desktop app is still being opened FROM the website, so the website origin
// is a meaningful, always-available source. Returns e.g. "http://localhost:3000".
const getWebsiteOrigin = () => {
  if (typeof window !== "undefined" && window.location && window.location.origin) {
    return window.location.origin;
  }
  return null;
};

const isExternalReferrer = () => {
  return (
    typeof document !== "undefined" &&
    document.referrer &&
    typeof window !== "undefined" &&
    !document.referrer.includes(window.location.hostname)
  );
};

// Detects the existing source.
// Resolution order:
//   1. the `source` query parameter (e.g. ?source=google), otherwise
//   2. the standard `utm_source` query parameter (e.g. ?utm_source=chatgpt.com),
//      which is what marketing/campaign links use, otherwise
//   3. the external referrer (document.referrer when it is not this site).
// Returns the detected source string, or null when none is available.
export const detectSource = (searchParams) => {
  const fromParam = searchParams?.get ? searchParams.get("source") : null;
  if (fromParam) {
    return fromParam;
  }
  const fromUtm = searchParams?.get ? searchParams.get("utm_source") : null;
  if (fromUtm) {
    return fromUtm;
  }
  if (isExternalReferrer()) {
    return document.referrer;
  }
  return null;
};

// Detects and, when present, persists the source for the current session so it
// can be reused later (e.g. when launching the Desktop app). Returns the
// detected source (or null). Never overwrites an existing stored source with
// null, so the first attribution wins for the whole session.
export const captureSource = (searchParams) => {
  const detected = detectSource(searchParams);
  try {
    if (detected && typeof window !== "undefined" && window.sessionStorage) {
      if (!window.sessionStorage.getItem(SOURCE_STORAGE_KEY)) {
        window.sessionStorage.setItem(SOURCE_STORAGE_KEY, detected);
      }
    }
  } catch (e) {
    // sessionStorage may be unavailable (private mode / blocked). Ignore.
  }
  return detected;
};

// Returns the retained source for the current session.
// Resolution order:
//   1. the retained attribution source captured earlier this session, else
//   2. a fresh detection from the current URL (?source= / external referrer), else
//   3. the website origin (so a launch FROM the website is always marked), else
//   4. SOURCE_FALLBACK ("unknown") as a last resort.
// This guarantees the desktop launch is always "properly marked" rather than
// landing on "unknown" when the user simply opened the site directly.
export const getSource = () => {
  try {
    if (typeof window !== "undefined" && window.sessionStorage) {
      const stored = window.sessionStorage.getItem(SOURCE_STORAGE_KEY);
      if (stored) {
        return stored;
      }
    }
  } catch (e) {
    // ignore storage access errors
  }

  if (typeof window !== "undefined" && window.location) {
    const detected = detectSource(new URLSearchParams(window.location.search));
    if (detected) {
      return detected;
    }
  }

  return getWebsiteOrigin() || SOURCE_FALLBACK;
};

// Builds the Desktop launch URL carrying the existing website source.
// Example: buildDesktopLaunchUrl("google") -> "formdepot://launch?source=google"
//
// `extraParams` lets the caller append the parameters the desktop app already
// relies on to open (e.g. id, email) without breaking the existing launch
// behavior. The source is always URL-encoded. When no source is available the
// existing fallback is used (never an invented value like "desktop").
export const buildDesktopLaunchUrl = (source, extraParams = {}) => {
  const value = source || SOURCE_FALLBACK;
  const parts = [`source=${encodeURIComponent(value)}`];
  Object.entries(extraParams).forEach(([key, val]) => {
    if (val !== undefined && val !== null && val !== "") {
      parts.push(`${key}=${encodeURIComponent(val)}`);
    }
  });
  return `formdepot://launch?${parts.join("&")}`;
};
